import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { initPushNotifications } from "../../Services/pushNotifications";
import { useSupabaseAuth } from "../AuthContext/AuthContext";
import { useNavigate } from "react-router-dom";
import { actions } from "../../Redux/store";
import { useSocket } from "../SocketContext/SocketProvider";
import { useLeagueStore } from "../../zustandStore/leagueStore";

interface InitPushNotificationContextType {
  initializePushNotifications: (userId?: string) => Promise<void>;
}

interface InitPushNotificationProviderProps {
  children: React.ReactNode;
}

const InitPushNotificationContext = createContext<
  InitPushNotificationContextType | undefined
>(undefined);

export const InitPushNotificationProvider: React.FC<
  InitPushNotificationProviderProps
> = ({ children }) => {
  const navigate = useNavigate();
  const { isConnected, startNewThread } = useSocket();
  const { selectedLeague } = useLeagueStore();
  const [pendingChatbotQuestion, setPendingChatbotQuestion] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to handle socket connection and open modal when connected
  useEffect(() => {
    if (isConnected && pendingChatbotQuestion) {
      console.log("Socket connected, opening chatbot modal with pending question");
      actions.modal.openChatbotSearchModal({ 
        suggestion: pendingChatbotQuestion 
      });
      console.log(`Chatbot search modal opened with suggestion: ${pendingChatbotQuestion}`);
      
      // Clear pending question and timeout
      setPendingChatbotQuestion(null);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [isConnected, pendingChatbotQuestion]);

  // Handler function for processing notification data and actions
  const handleNotificationData = (notificationData: any) => {
    console.log("Processing notification data:", notificationData);

    // Early return if no notification data
    if (!notificationData) {
      console.log("No notification data received");
      return;
    }

    // Determine notification type
    const getNotificationType = (data: any): string => {
      if (data.article_id) return 'ARTICLE';
      if (data.chatbot_hot_question) return 'CHATBOT_HOT_QUESTION';
      return 'UNKNOWN';
    };

    const notificationType = getNotificationType(notificationData);

    // Handle different notification types
    switch (notificationType) {
      case 'ARTICLE':
        handleArticleNotification(notificationData);
        break;

      case 'CHATBOT_HOT_QUESTION':
        handleChatbotHotQuestionNotification(notificationData);
        break;

      default:
        handleUnknownNotification(notificationData);
        break;
    }
  };

  // Handler for article notifications
  const handleArticleNotification = (data: any) => {
    console.log(`Processing article notification - ID: ${data.article_id}`);
    
    // Navigate to news route
    navigate("/news");
    
    // Wait for UI to load before opening modal for better UX
    setTimeout(() => {
      actions.modal.openNewsDetailsModal({ 
        article_id: data.article_id 
      });
      console.log(`News details modal opened for article: ${data.article_id}`);
    }, 800); // 800ms delay to allow page transition and UI loading
  };

  // Handler for chatbot hot question notifications
  const handleChatbotHotQuestionNotification = (data: any) => {
    console.log(`Processing chatbot hot question: ${data.chatbot_hot_question}`);
    
    // Navigate to chatbot AI route first
    navigate("/chatbot-ai");
    
    // Wait for UI to load, then handle socket connection
    setTimeout(() => {
      if (isConnected) {
        // Socket is already connected, open modal immediately
        console.log("Socket already connected, opening modal immediately");
        actions.modal.openChatbotSearchModal({ 
          suggestion: data.chatbot_hot_question 
        });
        console.log(`Chatbot search modal opened with suggestion: ${data.chatbot_hot_question}`);
      } else {
        // Socket not connected yet, store the question and wait for connection
        console.log("Socket not connected, storing question and waiting for connection...");
        setPendingChatbotQuestion(data.chatbot_hot_question);
        
        // Start a new thread to establish connection with league info
        startNewThread(selectedLeague?.leagueId, selectedLeague?.ownerId);
        
        // Set a timeout as fallback (10 seconds)
        timeoutRef.current = setTimeout(() => {
          console.warn("Socket connection timeout, opening modal anyway");
          actions.modal.openChatbotSearchModal({ 
            suggestion: data.chatbot_hot_question 
          });
          console.log(`Chatbot search modal opened with suggestion (timeout): ${data.chatbot_hot_question}`);
          setPendingChatbotQuestion(null);
        }, 10000);
      }
    }, 800); // 800ms delay to allow page transition and UI loading
  };

  // Handler for unknown/unhandled notification types
  const handleUnknownNotification = (data: any) => {
    console.warn("Unhandled notification type received:", data);
    
    // Optional: You can add analytics tracking here
    // analytics.track('unknown_notification_received', { data });
  };

  const { useAuthStore } = useSupabaseAuth();
  const user = useAuthStore((state: any) => state.user);

  const initializePushNotifications = async (userId?: string) => {

    console.log("Initializing push notifications",userId);
    const userIdToUse = userId || user?.user?.id;
    if (userIdToUse) {
      console.log("Initializing push notifications for user:", userIdToUse);
      await initPushNotifications(userIdToUse,handleNotificationData);
    } else {
      console.log("No user ID available for push notification initialization");
    }
  };

  // Initialize push notifications when the provider mounts
  // This will handle cases where the app is opened from a notification
  useEffect(() => {
    if (user?.user?.id) {
      initializePushNotifications(user.user.id);
    }
  }, [user?.user?.id]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <InitPushNotificationContext.Provider
      value={{
        initializePushNotifications,
      }}
    >
      {children}
    </InitPushNotificationContext.Provider>
  );
};

export const useInitPushNotification = () => {
  const context = useContext(InitPushNotificationContext);
  if (context === undefined) {
    throw new Error(
      "useInitPushNotification must be used within an InitPushNotificationProvider",
    );
  }
  return context;
};
